import { register } from "@rill/loader";

register(
  {
    name: "nutritionPlans",
    meta: ["no-bust", "nutrition-plan"],
    ttl: "2 hours",
  },
  async ({ api }, { proxyAthlete }) => {
    const query = {
      limit: false,
      sort: { utcEndDate: -1 },
      proxyAthlete,
    };

    const res = await api("nutrition-plan", { query });
    const data = await res.json();
    return data.sort((a, b) => b.utcEndDate - a.utcEndDate);
  }
);

register(
  {
    name: "mealPlan",
    meta: ["no-bust", "meal-plan"],
    ttl: "2 hours",
  },
  async ({ api }, { mealPlanId, proxyAthlete }) => {
    if (!mealPlanId) return null;
    const query = {
      proxyAthlete,
    };
    const res = await api(`meal-plan/${mealPlanId}`, { query });
    const data = await res.json();
    return data;
  }
);

register(
  {
    name: "mostUsedMealPlans",
    meta: ["no-bust", "food", "meal", "recipe"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const {
      activeUserToRequest,
      usersOrganization = { organizationActiveUsers: [] },
    } = locals;
    const { _id, organizationId } = activeUserToRequest;
    const { organizationActiveUsers = [] } = usersOrganization;
    const organizationIds = organizationActiveUsers.map(({ _id }) => _id);
    const query = {
      coachId: _id,
      ...(organizationId && { organizationIds }),
    };

    const request = await api(`most-used-meal-plans`, { query });
    const response = await request.json();
    const data = response?.response || {
      recipesResults: [],
      foodsResults: [],
      mealsResults: [],
      allResults: [],
    };
    return data;
  }
);

register(
  {
    name: "foodsByCoach",
    meta: ["no-bust", "food"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const { activeUserToRequest } = locals;
    const { _id } = activeUserToRequest;

    const query = {
      coachId: _id,
    };

    const request = await api("foods", { query });
    const response = await request.json();
    const data = response?.foods || [];
    return data;
  }
);

register(
  {
    name: "mealsByCoach",
    meta: ["no-bust", "meal"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const { activeUserToRequest } = locals;
    const { _id } = activeUserToRequest;

    const query = {
      coachId: _id,
    };

    const request = await api("meals", { query });
    const response = await request.json();
    const data = response?.meals || [];
    return data;
  }
);

register(
  {
    name: "basicMealsInformation",
    meta: ["no-bust", "meal"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const { activeUserToRequest } = locals;
    const { _id } = activeUserToRequest;

    const query = {
      coachId: _id,
    };

    const request = await api("basic-meals-information", { query });
    const response = await request.json();
    const data = response?.meals || [];
    return data;
  }
);

register(
  {
    name: "mealPlanIsTemplate",
    meta: ["no-bust", "meal-plan"],
    ttl: "2 hours",
  },
  async ({ api }, { mealPlanId }) => {
    if (!mealPlanId) return false;
    const res = await api(`meal-plan-is-template/${mealPlanId}`);
    const data = await res.json();
    const { hasTemplate = false } = data;
    return hasTemplate;
  }
);

register(
  {
    name: "mealPlanTemplates",
    meta: ["no-bust", "meal-plan"],
    ttl: "2 hours",
  },
  async (
    { api, locals },
    { customLimit, customSkip, noPagination, planMode, sort }
  ) => {
    try {
      const { activeUserToRequest } = locals;
      const { _id } = activeUserToRequest;
      const query = {
        coachId: _id,
        customLimit,
        customSkip,
        noPagination,
        ...(planMode && { planMode }),
        ...(sort && { sort }),
      };
      const res = await api(`meal-plan-template`, { query });
      if (!res.ok) {
        return [];
      }
      const text = await res.text();
      if (!text || text.trim() === "") {
        return [];
      }
      const data = JSON.parse(text);
      const { mealPlans = [] } = data;
      return mealPlans;
    } catch (e) {
      console.error("Error loading meal plan templates:", e);
      return [];
    }
  }
);

register(
  {
    name: "assignedMealPlans",
    meta: ["no-bust", "meal-plan"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const { activeUserToRequest } = locals;
    const { _id } = activeUserToRequest;
    const query = {
      coachId: _id,
    };
    const res = await api(`assigned-meal-plans`, { query });
    const data = await res.json();
    const { mealPlans = [] } = data;
    return mealPlans;
  }
);

register(
  {
    name: "mealPlanCount",
    meta: ["no-bust", "meal-plan"],
    ttl: "2 hours",
  },
  async ({ api, locals }, { planMode } = {}) => {
    const { activeUserToRequest } = locals;
    const { _id } = activeUserToRequest;
    const query = {
      coachId: _id,
      ...(planMode && { planMode }),
    };
    const res = await api(`meal-plan-template-count`, { query });
    const data = await res.json();
    const { mealPlanCount = 0 } = data;
    return mealPlanCount;
  }
);

register(
  {
    name: "recipeCount",
    meta: ["no-bust", "recipe"],
    ttl: "2 hours",
  },
  async ({ api }) => {
    try {
      const res = await api("recipe-count");
      if (!res.ok) {
        return 0;
      }
      const text = await res.text();
      if (!text || text.trim() === "") {
        return 0;
      }
      const data = JSON.parse(text);
      const { publishedCount = 0 } = data;
      return publishedCount;
    } catch (e) {
      console.error("Error loading recipe count:", e);
      return 0;
    }
  }
);

register(
  {
    name: "groceryList",
    meta: ["no-bust", "meal-plan"],
    ttl: "1 minute",
  },
  async ({ api }, { mealPlanId, week }) => {
    if (!mealPlanId || week === undefined) {
      return null;
    }
    try {
      const res = await api("generate-grocery-list", {
        method: "POST",
        body: {
          mealPlanId,
          week,
        },
      });
      const data = await res.json();
      return data;
    } catch (e) {
      console.error("Error loading grocery list:", e);
      return null;
    }
  }
);

register(
  {
    name: "customFoods",
    meta: ["no-bust", "food", "recipe", "meal"],
    ttl: "2 hours",
  },

  async ({ api, locals }) => {
    const {
      activeUserToRequest,
      usersOrganization = { organizationActiveUsers: [] },
    } = locals;
    const { _id: ownerId } = activeUserToRequest;
    const { organizationActiveUsers = [] } = usersOrganization;
    const organizationIds = organizationActiveUsers.map(({ _id }) => _id);
    const query = {
      ownerId,
      organizationIds,
    };

    const request = await api(`meal-planner-custom-foods`, { query });
    const response = await request.json();
    const data = response?.data || [];
    return data;
  }
);
const RECIPE_DETAILS_ENDPOINTS = {
  recipe: "recipe",
  "public-recipe": "public-recipe",
  public_recipe: "public-recipe",
};

register(
  {
    name: "recipeDetails",
    meta: ["no-bust", "recipe"],
    ttl: "2 hours",
  },
  async ({ api }, { type, recipeId }) => {
    const endpoint = RECIPE_DETAILS_ENDPOINTS[type];
    if (!endpoint || !recipeId) return null;

    const res = await api(`${endpoint}/${encodeURIComponent(recipeId)}`);
    if (!res.ok) return null;

    const recipe = await res.json().catch(() => null);
    return recipe?._id ? recipe : null;
  }
);
register(
  {
    name: "mealPhotos",
    meta: ["no-bust", "meal-plan", "thread"],
    ttl: "2 hours",
  },
  async ({ api, locals }, query) => {
    const { activeUserToRequest, athlete } = locals;
    const { _id } = activeUserToRequest;
    let coachId;
    let athleteId;
    if (athlete) {
      coachId = _id;
      athleteId = athlete._id;
    } else {
      coachId = activeUserToRequest.coach._id;
      athleteId = _id;
    }
    const res = await api("meal-photos-by-date", {
      query: { ...query, coachId, athleteId },
    });
    const data = await res.json();

    return data;
  }
);

register(
  {
    name: "externalFavoriteIds",
    meta: ["no-bust", "recipe"],
    ttl: "1 minute",
  },
  async ({ api }) => {
    try {
      const res = await api("spoonacular/recipes/favorite-ids");
      if (!res.ok) {
        return [];
      }
      const data = await res.json();
      const ids = Array.isArray(data)
        ? data
        : Array.isArray(data?.data?.ids)
          ? data.data.ids
          : Array.isArray(data?.ids)
            ? data.ids
            : [];
      return ids;
    } catch (e) {
      console.error("Error loading external favorite ids:", e);
      return [];
    }
  }
);
